import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Plane, Plus, MessageCircle, Send, Trash2, X, Calendar, MapPin, Phone, Car, Loader2
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const KIND_OPTIONS = [
  { value: 'offering', label: 'Offering a ride', desc: "I'm driving / have space" },
  { value: 'needing', label: 'Need a ride', desc: 'Looking for a ride to/from' },
  { value: 'sharing', label: 'Split rideshare', desc: 'Sharing Uber/Lyft/Taxi' },
];

const KIND_STYLES = {
  offering: {
    chip: 'text-emerald-700',
    avatar: 'bg-emerald-100 text-emerald-700',
    activeBtn: 'border-emerald-300 bg-emerald-50 ring-2 ring-emerald-200',
  },
  needing: {
    chip: 'text-amber-700',
    avatar: 'bg-amber-100 text-amber-700',
    activeBtn: 'border-amber-300 bg-amber-50 ring-2 ring-amber-200',
  },
  sharing: {
    chip: 'text-blue-700',
    avatar: 'bg-blue-100 text-blue-700',
    activeBtn: 'border-blue-300 bg-blue-50 ring-2 ring-blue-200',
  },
};

const kindMeta = (kind) => KIND_OPTIONS.find(k => k.value === kind) || KIND_OPTIONS[0];
const kindStyles = (kind) => KIND_STYLES[kind] || KIND_STYLES.offering;

const TravelBoard = ({ attendeeId }) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    kind: 'offering',
    travel_date: '',
    departure_location: '',
    contact_info: '',
    message: '',
  });
  const [replyDrafts, setReplyDrafts] = useState({}); // postId -> text
  const [replyBusy, setReplyBusy] = useState(null);

  const fetchPosts = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/retreat/travel-board`);
      setPosts(res.data);
    } catch {
      // silent — board is non-critical
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  const submitPost = async () => {
    if (!form.message.trim()) {
      toast.error('Add a message to your post');
      return;
    }
    setCreating(true);
    try {
      await axios.post(`${BACKEND_URL}/api/retreat/travel-board`, {
        attendee_id: attendeeId,
        ...form,
      });
      toast.success('Posted to the board');
      setShowForm(false);
      setForm({ kind: 'offering', travel_date: '', departure_location: '', contact_info: '', message: '' });
      fetchPosts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not post');
    } finally {
      setCreating(false);
    }
  };

  const submitReply = async (postId) => {
    const message = (replyDrafts[postId] || '').trim();
    if (!message) return;
    setReplyBusy(postId);
    try {
      await axios.post(`${BACKEND_URL}/api/retreat/travel-board/${postId}/replies`, {
        attendee_id: attendeeId,
        message,
      });
      setReplyDrafts({ ...replyDrafts, [postId]: '' });
      fetchPosts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not reply');
    } finally {
      setReplyBusy(null);
    }
  };

  const deletePost = async (postId) => {
    if (!window.confirm('Delete this post? Replies will also be removed.')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/retreat/travel-board/${postId}`, {
        params: { attendee_id: attendeeId },
      });
      toast.success('Post deleted');
      fetchPosts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not delete');
    }
  };

  const timeAgo = (iso) => {
    if (!iso) return '';
    const diff = Date.now() - new Date(iso).getTime();
    const minutes = Math.floor(diff / 60000);
    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <Card className="border-0 shadow-sm" data-testid="travel-board-card">
      <CardContent className="p-5 sm:p-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Car className="w-4 h-4 text-gray-400" />
              <p className="eyebrow text-gray-400">Travel Board</p>
            </div>
            <p className="text-sm text-gray-500">
              Coordinate rides, share airport runs, split rideshares.
            </p>
          </div>
          {!showForm && (
            <Button
              size="sm"
              onClick={() => setShowForm(true)}
              className="bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 shrink-0 btn-lift"
              data-testid="travel-new-post-btn"
            >
              <Plus className="w-3.5 h-3.5 mr-1.5" />
              New Post
            </Button>
          )}
        </div>

        {/* New-post form */}
        {showForm && (
          <div className="border border-gray-200 rounded-xl p-4 mb-5 bg-gray-50/50" data-testid="travel-new-post-form">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-semibold text-gray-800">Post a travel update</p>
              <button
                onClick={() => setShowForm(false)}
                className="text-gray-400 hover:text-gray-700"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Kind chooser */}
            <div className="grid grid-cols-3 gap-2 mb-3">
              {KIND_OPTIONS.map(k => {
                const active = form.kind === k.value;
                const s = kindStyles(k.value);
                return (
                  <button
                    key={k.value}
                    type="button"
                    onClick={() => setForm({ ...form, kind: k.value })}
                    className={`text-left rounded-lg border p-2.5 transition-all
                      ${active ? s.activeBtn : 'border-gray-200 bg-white hover:border-gray-300'}`}
                    data-testid={`travel-kind-${k.value}`}
                  >
                    <p className="text-xs font-semibold text-gray-900">{k.label}</p>
                    <p className="text-[10px] text-gray-500 mt-0.5 leading-tight">{k.desc}</p>
                  </button>
                );
              })}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
              <div className="space-y-1">
                <Label className="text-xs text-gray-500">When</Label>
                <Input
                  value={form.travel_date}
                  onChange={(e) => setForm({ ...form, travel_date: e.target.value })}
                  placeholder="e.g. Fri Sept 18, ~2pm"
                  className="h-9 text-sm"
                  data-testid="travel-date-input"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-gray-500">From / to</Label>
                <Input
                  value={form.departure_location}
                  onChange={(e) => setForm({ ...form, departure_location: e.target.value })}
                  placeholder="e.g. MSP airport"
                  className="h-9 text-sm"
                  data-testid="travel-location-input"
                />
              </div>
            </div>

            <div className="space-y-1 mb-3">
              <Label className="text-xs text-gray-500">Contact (optional)</Label>
              <Input
                value={form.contact_info}
                onChange={(e) => setForm({ ...form, contact_info: e.target.value })}
                placeholder="Phone, email, or text 'reply here'"
                className="h-9 text-sm"
                data-testid="travel-contact-input"
              />
            </div>

            <div className="space-y-1 mb-4">
              <Label className="text-xs text-gray-500">Message *</Label>
              <Textarea
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="Driving up Friday from Minneapolis. Have 2 seats open — happy to swing by MSP for pickup."
                rows={3}
                className="text-sm resize-none"
                data-testid="travel-message-input"
              />
            </div>

            <div className="flex gap-2 justify-end">
              <Button size="sm" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
              <Button
                size="sm"
                onClick={submitPost}
                disabled={creating}
                className="bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600"
                data-testid="travel-submit-post-btn"
              >
                {creating ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1.5" />}
                Post
              </Button>
            </div>
          </div>
        )}

        {/* Posts list */}
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-5 h-5 text-gray-300 animate-spin" />
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-10 px-4 rounded-xl bg-gray-50 border border-dashed border-gray-200">
            <Plane className="w-7 h-7 text-gray-300 mx-auto mb-3" />
            <p className="text-sm font-medium text-gray-700">No posts yet</p>
            <p className="text-xs text-gray-500 mt-1 max-w-sm mx-auto">
              Be the first to coordinate. Drop your travel plans below and connect with other brothers heading to camp.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {posts.map(post => {
              const meta = kindMeta(post.kind);
              const s = kindStyles(post.kind);
              const isMine = post.attendee_id === attendeeId;
              return (
                <div
                  key={post.id}
                  className="border border-gray-200 rounded-xl p-4 hover:border-gray-300 transition-colors"
                  data-testid={`travel-post-${post.id}`}
                >
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className={`w-8 h-8 rounded-full ${s.avatar} flex items-center justify-center shrink-0`}>
                        <span className="text-xs font-bold">
                          {post.author_name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate">
                          {post.author_name}
                          {isMine && <span className="ml-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">· You</span>}
                        </p>
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className={`inline-block text-[10px] font-bold uppercase tracking-wider ${s.chip}`}>
                            {meta.label}
                          </span>
                          <span className="text-[10px] text-gray-400">·</span>
                          <span className="text-[10px] text-gray-400">{timeAgo(post.created_at)}</span>
                        </div>
                      </div>
                    </div>
                    {isMine && (
                      <button
                        onClick={() => deletePost(post.id)}
                        className="text-gray-300 hover:text-red-500 transition-colors shrink-0"
                        aria-label="Delete"
                        data-testid={`travel-delete-${post.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>

                  <p className="text-sm text-gray-700 leading-relaxed mb-2 whitespace-pre-wrap">{post.message}</p>

                  {(post.travel_date || post.departure_location || post.contact_info) && (
                    <div className="flex flex-wrap gap-x-3 gap-y-1 mb-2 text-xs text-gray-500">
                      {post.travel_date && (
                        <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" />{post.travel_date}</span>
                      )}
                      {post.departure_location && (
                        <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{post.departure_location}</span>
                      )}
                      {post.contact_info && (
                        <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{post.contact_info}</span>
                      )}
                    </div>
                  )}

                  {/* Replies */}
                  {post.replies && post.replies.length > 0 && (
                    <div className="mt-3 pl-3 border-l-2 border-gray-100 space-y-2">
                      {post.replies.map(r => (
                        <div key={r.id} className="text-xs" data-testid={`travel-reply-${r.id}`}>
                          <p className="text-gray-800">
                            <span className="font-semibold">{r.author_name}</span>
                            <span className="text-gray-400 ml-1.5">{timeAgo(r.created_at)}</span>
                          </p>
                          <p className="text-gray-600 mt-0.5 whitespace-pre-wrap">{r.message}</p>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Reply input */}
                  <div className="mt-3 flex gap-2">
                    <Input
                      value={replyDrafts[post.id] || ''}
                      onChange={(e) => setReplyDrafts({ ...replyDrafts, [post.id]: e.target.value })}
                      placeholder="Reply…"
                      className="h-8 text-xs"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          submitReply(post.id);
                        }
                      }}
                      data-testid={`travel-reply-input-${post.id}`}
                    />
                    <Button
                      size="sm"
                      onClick={() => submitReply(post.id)}
                      disabled={replyBusy === post.id || !(replyDrafts[post.id] || '').trim()}
                      variant="outline"
                      className="h-8 px-3 shrink-0"
                      data-testid={`travel-reply-submit-${post.id}`}
                    >
                      {replyBusy === post.id ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <MessageCircle className="w-3 h-3" />
                      )}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TravelBoard;
